/**
 * ADA / Accessibility Type presentation shared by the management tables that
 * surface them (Manage Applications, Manage Unit Availability, Manage Rent
 * Roll, Portfolio Manage Expiring Leases).
 *
 * Both columns and their CSV exports read from these helpers so a table and its
 * export can never disagree. The backend sends `isADA` as a boolean for a unit
 * (`null` from Manage Applications when no unit is assigned) and
 * `accessibilityTypes` as an already-alphabetical array of names.
 */

export const ADA_YES = 'Yes';
export const ADA_NO = 'No';

/**
 * Yes/No for a unit's ADA status. Without a unit there is no status to report,
 * so a missing value renders blank rather than as a misleading "No".
 */
export const formatADA = (isADA) => {
  if (isADA === null || isADA === undefined) return null;

  return isADA ? ADA_YES : ADA_NO;
};

/**
 * Comma-separated, alphabetical list of accessibility types.
 *
 * A unit that is not ADA accessible — or an ADA unit with nothing selected —
 * renders blank, matching the not-applicable convention the surrounding columns
 * already use.
 */
export const formatAccessibilityTypes = (isADA, accessibilityTypes) => {
  if (!isADA) return null;

  const names = (accessibilityTypes ?? []).filter(Boolean);
  if (!names.length) return null;

  return [...names].sort((a, b) => a.localeCompare(b)).join(', ');
};

export const ADA_FILTER_OPTIONS = [
  { text: ADA_YES, value: ADA_YES },
  { text: ADA_NO, value: ADA_NO },
];

/**
 * Column ids these tables use for the two new columns. Shared so flag gating
 * and the stored-column-order migration below can never drift apart.
 */
export const ADA_COLUMN_IDS = ['ada', 'accessibilityType'];

/**
 * The two column definitions, shared by every table that shows them. Labels
 * differ per table (FormattedMessage, intl.formatMessage or a plain string), so
 * the caller supplies them; everything else — ids, filter options, sort/search
 * behaviour — stays identical by construction.
 */
export const adaHeaders = ({ adaLabel, accessibilityTypeLabel }) => [
  {
    id: 'ada',
    label: adaLabel,
    filterOptions: ADA_FILTER_OPTIONS,
    dataType: 'string',
    sortable: true,
    multiselect: true,
  },
  {
    id: 'accessibilityType',
    label: accessibilityTypeLabel,
    dataType: 'string',
    sortable: true,
    searchable: true,
    multiselect: true,
  },
];

/**
 * The Accessibility Type cell, shared by every table that shows it.
 *
 * A multi-type value is wider than the column, so the full text is served by a
 * tooltip instead of by letting the cell grow. Keeping it from growing is what
 * the `sx` is for: the tooltip wrapper the component library renders inside the
 * cell is a block element that wraps by default, so a two-type value would
 * become two centred lines and stop sitting on the baseline of the single-line
 * cells beside it. Every other column here — Status, Resident — stays on one
 * line and truncates, so this one is pinned to the same behaviour rather than
 * being the only column that grows.
 */
export const accessibilityTypeCell = (value) => ({
  value,
  tooltip: Boolean(value),
  tooltipContent: value ?? '',
  sx: {
    '& > span > div': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
});

/** Sort/filter path entries for the two columns. */
export const ADA_PATH_MAP = {
  ada: { path: 'ada', dataType: 'string' },
  accessibilityType: { path: 'accessibilityType', dataType: 'string' },
};

/**
 * Display values the two columns sort, filter and export on, derived from the
 * backend's `isADA` / `accessibilityTypes` pair.
 */
export const adaDisplayFields = (source) => ({
  ada: formatADA(source?.isADA),
  accessibilityType: formatAccessibilityTypes(
    source?.isADA,
    source?.accessibilityTypes,
  ),
});

/**
 * A `${tableName}_column-order` entry in localStorage written before these
 * columns existed does not mention them, and the table would silently drop
 * them. Force a rewrite of the stored order whenever it cannot be trusted to
 * place them: when there is no entry at all, when it cannot be parsed, or when
 * the columns are enabled and the stored order is missing either of them.
 *
 * With the columns disabled an existing order is left alone, so a user who has
 * since moved or hidden them keeps their configuration.
 */
export const shouldForceADAColumnOrder = ({ tableName, showADAColumns }) => {
  try {
    const storedOrder = localStorage.getItem(`${tableName}_column-order`);
    if (!storedOrder) return true;
    if (!showADAColumns) return false;

    const parsedOrder = JSON.parse(storedOrder);
    return ADA_COLUMN_IDS.some(
      (columnId) => parsedOrder[columnId] === undefined,
    );
  } catch {
    return true;
  }
};
